<script setup lang="ts">
  import { useRoute } from 'vue-router';
  import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
  import { ActionButtons, SimpleLoading } from '@/shared/ui';
  import { BranchFormSections, type BranchThemeColors } from '@/pages/admin/ui';
  import { useImageUploadStore } from '@/features/Document';
  import { useBranchManagement } from '@/pages/admin';
  import { useUserStore } from '@/entities/User';
  import { useCustomToast } from '@/shared/lib';
  import { useModal } from 'vue-final-modal';
  import ConfirmationModal from '@/shared/ui/modals/ConfirmationModal.vue';
  import BranchViewLogo from '../BranchViewLogo.vue';
  import Trash from '@/shared/assets/icons/TrashIcon.svg';

  const route = useRoute();
  const filialId = route.params.id;

  const { loadBranch, isLoading, navigateToList } = useBranchManagement();
  const toast = useCustomToast();
  const filialName = ref('');
  const isFormValid = ref(false);

  const primaryColor = ref('#F8F4F4');
  const secondaryColor = ref('#854149');
  const textColor = ref('#2F2F2F');
  const primaryColorDark = ref('');
  const secondaryColorDark = ref('');
  const textColorDark = ref('');
  const logoPath = ref('');
  const logoName = ref('');
  const areColorsSelected = ref(true);

  const {
    images,
    hasImages,
    allUploadsComplete,
    handleImageUpload,
    handleImageProgress,
    handleImageComplete,
    clearState,
    removeImage,
    updateBranch: updateBranchWithImage,
    loadingRequest,
  } = useImageUploadStore;

  const originalData = ref({
    name: '',
    colorPrimary: '',
    colorSecondary: '',
    colorText: '',
    colorPrimaryDark: '',
    colorSecondaryDark: '',
    colorTextDark: '',
    logoPath: '',
    logoName: '',
  });

  const showDuplicateNameErrorToast = () => {
    toast.showErrorToast(
      {
        title: 'Este nome de Filial já existe',
        message: '',
        type: 'error',
      },
    );
  };

  const handleAllFilesDeleted = () => {
    hasImages.value = false;
    allUploadsComplete.value = false;
  };

  const handleColorUpdate = (colors: BranchThemeColors) => {
    primaryColor.value = colors.primaryColor;
    secondaryColor.value = colors.secondaryColor;
    textColor.value = colors.textColor;
    primaryColorDark.value = colors.primaryColorDark;
    secondaryColorDark.value = colors.secondaryColorDark;
    textColorDark.value = colors.textColorDark;
    areColorsSelected.value = true;
    validateUpdateForm();
  };

  const hasNewImage = () => {
    return hasImages.value && images.value.some((img: any) => img instanceof File && img.size > 0);
  };

  const validateUpdateForm = () => {
    const hasName = !!filialName.value.trim();
    const hasLogo = !!logoPath.value || hasNewImage();
    const hasRequiredFields = hasName && hasLogo && areColorsSelected.value;

    const hasNameChange = filialName.value !== originalData.value.name;

    const hasColorChanges =
      primaryColor.value !== originalData.value.colorPrimary ||
      secondaryColor.value !== originalData.value.colorSecondary ||
      textColor.value !== originalData.value.colorText ||
      primaryColorDark.value !== originalData.value.colorPrimaryDark ||
      secondaryColorDark.value !== originalData.value.colorSecondaryDark ||
      textColorDark.value !== originalData.value.colorTextDark;

    const hasLogoChange = logoPath.value !== originalData.value.logoPath;

    const hasChanges = hasNameChange || hasColorChanges || hasLogoChange || hasNewImage();

    isFormValid.value = hasChanges && hasRequiredFields;
  };

  const collectChangedData = () => {
    const changedData: Partial<{
      name: string;
      colorPrimary: string;
      colorSecondary: string;
      colorText: string;
      colorPrimaryDark: string;
      colorSecondaryDark: string;
      colorTextDark: string;
      logoPath: string;
    }> = {};

    if (filialName.value !== originalData.value.name) {
      changedData.name = filialName.value;
    }

    if (primaryColor.value !== originalData.value.colorPrimary) {
      changedData.colorPrimary = primaryColor.value;
    }

    if (secondaryColor.value !== originalData.value.colorSecondary) {
      changedData.colorSecondary = secondaryColor.value;
    }

    if (textColor.value !== originalData.value.colorText) {
      changedData.colorText = textColor.value;
    }

    if (primaryColorDark.value !== originalData.value.colorPrimaryDark) {
      changedData.colorPrimaryDark = primaryColorDark.value;
    }

    if (secondaryColorDark.value !== originalData.value.colorSecondaryDark) {
      changedData.colorSecondaryDark = secondaryColorDark.value;
    }

    if (textColorDark.value !== originalData.value.colorTextDark) {
      changedData.colorTextDark = textColorDark.value;
    }

    if (logoPath.value !== originalData.value.logoPath) {
      changedData.logoPath = logoPath.value;
    }

    return changedData;
  };

  const isDuplicateNameError = (error: any): boolean => {
    const errors = error?.response?.data?.errors;
    if (!errors || typeof errors !== 'object') return false;

    const duplicateKeywords = [
      'Este nome de Filial já existe',
      'nome de Filial já existe',
      'já existe',
    ];

    return Object.values(errors).some(
      (errorArray: any) =>
        Array.isArray(errorArray) &&
        errorArray.some((errorMsg: string) =>
          duplicateKeywords.some((keyword) => errorMsg.includes(keyword))
        )
    );
  };

  const handleUpdateSuccess = async () => {
    const userStore = useUserStore();
    await userStore.me();
    toast.showSuccessToast({
      title: 'Edição salva com sucesso',
      message: '',
      type: 'success',
    });
    clearState();
    navigateToList();
  };

  const handleUpdateError = (error: any) => {
    console.error('Error updating branch:', error);

    if (isDuplicateNameError(error)) {
      showDuplicateNameErrorToast();
    } else {
      toast.showErrorToast(
        {
          title: 'Erro ao salvar edição. Por favor, tente novamente.',
          message: '',
          type: 'error',
        },
      );
    }
  };

  const handleUpdateData = async () => {
    if (!isFormValid.value) return;

    const changedData = collectChangedData();
    const hasDataChanges = Object.keys(changedData).length > 0;
    const hasImageChange = hasNewImage();

    if (!hasDataChanges && !hasImageChange) {
      navigateToList();
      return;
    }

    try {
      await updateBranchWithImage(filialId as string, changedData);

      if (loadingRequest.value === 'success') {
        await handleUpdateSuccess();
      }
    } catch (error: any) {
      handleUpdateError(error);
    }
  };

  const handleViewLogo = () => {
    const { open: openViewLogo, close: closeViewLogo } = useModal({
      component: BranchViewLogo,
      attrs: {
        title: 'Visualização',
        text: 'Visualização da logo da Filial',
        logoPath: logoPath.value,
        branchId: Number(filialId),
        onClose() {
          closeViewLogo();
        },
      },
    });

    openViewLogo();
  };

  const handleDeleteLogo = () => {
    const { open: openConfirmation, close: closeConfirmation } = useModal({
      component: ConfirmationModal,
      attrs: {
        icon: Trash,
        iconAlt: 'Icone de Deleção',
        title: 'Tem certeza que deseja remover o logo da Filial?',
        text: 'A ação só será concluída após a edição da filial.',
        actionButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        onClose() {
          closeConfirmation();
        },
        async onConfirm() {
          logoPath.value = '';
          logoName.value = '';
          validateUpdateForm();
          closeConfirmation();
        },
      },
    });

    openConfirmation();
  };

  watch([filialName, hasImages], () => {
    validateUpdateForm();
  });

  onMounted(async () => {
    clearState();
    const branch = await loadBranch(filialId as string);

    if (branch) {
      originalData.value = {
        name: branch.name,
        colorPrimary: branch.colorPrimary || '#F8F4F4',
        colorSecondary: branch.colorSecondary || '#854149',
        colorText: branch.colorText || '#2F2F2F',
        colorPrimaryDark: branch.colorPrimaryDark ?? '',
        colorSecondaryDark: branch.colorSecondaryDark ?? '',
        colorTextDark: branch.colorTextDark ?? '',
        logoPath: branch.logoPath || '',
        logoName: branch.logoMinioObjectName?.split('/').pop() || '',
      };

      filialName.value = branch.name;
      primaryColor.value = branch.colorPrimary || '#F8F4F4';
      secondaryColor.value = branch.colorSecondary || '#854149';
      textColor.value = branch.colorText || '#2F2F2F';
      primaryColorDark.value = branch.colorPrimaryDark ?? '';
      secondaryColorDark.value = branch.colorSecondaryDark ?? '';
      textColorDark.value = branch.colorTextDark ?? '';
      logoPath.value = branch.logoPath || '';
      logoName.value = branch.logoMinioObjectName?.split('/').pop() || '';

      validateUpdateForm();
    }
  });

  onBeforeUnmount(() => {
    if (images.value.length > 0) {
      const imageToRemove = images.value[0];
      removeImage(imageToRemove);
    }

    images.value = [];
    hasImages.value = false;
    allUploadsComplete.value = false;
    clearState();
  });
</script>

<template>
  <section class="pt-4 p-6 text-gray-07">
    <div class="mt-4 mb-6 text-xl font-semibold flex justify-start">
      <span>Editar Filial</span>
    </div>

    <div
      v-if="isLoading"
      class="flex justify-center items-center h-[75vh] py-10"
    >
      <SimpleLoading />
    </div>

    <div
      v-else
      class="w-full border border-border-color divide-y divide-gray-02 rounded-2xl text-sm bg-white-01"
    >
      <div class="p-6">
        <div class="h-auto flex flex-col gap-2">
          <div class="font-semibold flex">
            Nome da Filial
          </div>
          <input
            v-model="filialName"
            class="rounded-xl border flex items-center p-4 border-border-color bg-white-01"
            placeholder="Escreva o nome que representa a filial"
          >
        </div>
      </div>

      <BranchFormSections
        :primary-color="primaryColor"
        :secondary-color="secondaryColor"
        :text-color="textColor"
        :primary-color-dark="primaryColorDark"
        :secondary-color-dark="secondaryColorDark"
        :text-color-dark="textColorDark"
        :logo-path="logoPath"
        :logo-name="logoName"
        :all-uploads-complete="allUploadsComplete"
        @files-selected="handleImageUpload"
        @upload-progress="handleImageProgress"
        @upload-complete="(_, imageIds) => handleImageComplete(imageIds)"
        @all-files-deleted="handleAllFilesDeleted"
        @colors-changed="handleColorUpdate"
        @view-logo="handleViewLogo"
        @delete-logo="handleDeleteLogo"
      />
    </div>
    <ActionButtons
      v-if="!isLoading"
      :disabled="!isFormValid"
      :loading="loadingRequest === 'loading'"
      cancel-route="/admin/filiais"
      @save="handleUpdateData"
    />
  </section>
</template>
